/**
 * Sticky Cart Handler - Pure Vanilla JS
 * HTML: snippets/sticky-cart-bar.liquid
 * Ultra-optimized, dynamic, high-performance
 */

(function() {
  'use strict';

  // CONFIG
  const cfg = window.STICKY_CART_CONFIG || { bundlePrice: 0, mandatoryLimit: 3 };

  // CACHE: DOM elements
  const el = {};
  const getEl = id => el[id] || (el[id] = document.getElementById(id));

  // STATE
  let selected = [];
  let updateTimer = null;

  // HELPERS
  const fmt = (paise) => (paise / 100).toFixed(0);
  const fmtDec = (paise) => (paise / 100).toFixed(2);
  const debounce = (fn, ms = 100) => {
    return () => {
      clearTimeout(updateTimer);
      updateTimer = setTimeout(fn, ms);
    };
  };

  // SCROLLBAR WIDTH CALCULATION
  const getScrollbarWidth = () => {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.cssText = 'width: 99px; height: 99px; overflow: scroll; position: absolute; top: -9999px;';
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
  };

  let scrollbarWidth = 0;
  // Calculate once on init
  window.addEventListener('DOMContentLoaded', () => {
    scrollbarWidth = getScrollbarWidth();
  }, { once: true });

  // GET IMAGE
  const getImg = (item) => {
    const sels = ['.product-image img', '.product-image picture img', 'img[alt]', 'img'];
    for (const s of sels) {
      const img = item.querySelector(s);
      if (img?.src) return img.src;
    }
    return '';
  };

  // POPUP
  const openPopup = () => {
    // Set scrollbar width CSS variable before opening popup
    if (!scrollbarWidth) scrollbarWidth = getScrollbarWidth();
    document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
    // Save current scroll position and freeze background
    document.documentElement.style.setProperty('--scroll-top', '-' + window.scrollY + 'px');

    getEl('flexi-popup').style.display = 'block';
    getEl('flexi-popup-overlay').style.display = 'block';
    document.body.classList.add('flexi-popup-open');
  };

  const closePopup = () => {
    const popup = getEl('flexi-popup');
    if (!popup || popup.style.display === 'none') return;
    // Restore scroll position before removing fixed positioning
    const scrollY = document.documentElement.style.getPropertyValue('--scroll-top');
    popup.style.display = 'none';
    getEl('flexi-popup-overlay').style.display = 'none';
    document.body.classList.remove('flexi-popup-open');
    // Reset scrollbar width variable
    document.documentElement.style.setProperty('--scrollbar-width', '0px');
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  };

  // UPDATE SELECTED PRODUCTS
  const update = () => {
    selected = [];
    let total = 0;

    // Get all quantity inputs with values > 0
    document.querySelectorAll('.quantity-input').forEach(quantityInput => {
      const qty = parseInt(quantityInput.value || 0);
      // Skip if quantity is 0 or less
      if (qty <= 0) return;

      const item = quantityInput.closest('.collection-item');
      if (!item) return;

      const checkbox = item.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      const price = parseInt(checkbox.dataset.price || 0);
      const title = checkbox.value.split('/')[0];
      const image = getImg(item);
      const stepIndex = item.dataset.stepIndex || '0';

      // Create duplicates for each quantity
      for (let i = 0; i < qty; i++) {
        selected.push({ title, image, price, checkbox, quantityInput, stepIndex });
        total += price;
      }
    });

    render(total);
  };

  // RENDER STICKY BAR
  const render = (total) => {
    const count = selected.length;
    const rem = cfg.mandatoryLimit - count;
    const title = getEl('flexi-title');
    const countEl = getEl('flexi-count');
    const mandatoryCounter = getEl('flexi-mandatory-counter');
    const msgEl = getEl('flexi-message');
    const priceSpan = getEl('flexi-price-amount');
    const dealPrice = priceSpan?.textContent?.replace('₹', '') || fmt(cfg.bundlePrice);

    // Always ensure countEl is hidden when count is 0
    if (count === 0) {
      // Initial state - NO items
      title.style.display = 'block';
      countEl.style.display = 'none';
      mandatoryCounter.style.display = 'block';
      countEl.textContent = '0/' + cfg.mandatoryLimit + ' Added'; // Reset to prevent stale text
      if (cfg.isMultiStep && window.FLEXI_STEP_LABELS_TEXT) {
        msgEl.innerHTML = `Add ${window.FLEXI_STEP_LABELS_TEXT}`;
      } else {
        msgEl.innerHTML = `Add Any <span id="flexi-mandate">${cfg.mandatoryLimit}</span> Perfumes`;
      }
    } else {
      // Items added - show count
      title.style.display = 'none';
      countEl.style.display = 'block';
      mandatoryCounter.style.display = 'none';
      countEl.textContent = `${count}/${cfg.mandatoryLimit} Added`;

      if (cfg.isMultiStep && window.FLEXI_STEPS) {
        // Multi-step: build per-step remaining message
        const stepsArr = window.FLEXI_STEPS;
        const incomplete = [];
        stepsArr.forEach(step => {
          const stepEl = document.querySelector(`.flexi-step[data-step-index="${step.index}"]`);
          if (!stepEl) return;
          let stepCount = 0;
          stepEl.querySelectorAll('.quantity-input').forEach(inp => {
            stepCount += parseInt(inp.value) || 0;
          });
          const rem = step.mandatory - stepCount;
          if (rem > 0) {
            const labelName = step.label.replace(/^any\s+/i, '').replace(/^\d+\s*/, '').trim();
            incomplete.push(`${rem} ${labelName}`);
          }
        });
        msgEl.textContent = incomplete.length > 0
          ? `Add ${incomplete.join(' + ')} to Unlock ₹${dealPrice}`
          : `You've Unlocked ₹${dealPrice} Deal Price`;
          msgEl.style.marginBottom = '10px';
      } else {
        msgEl.textContent = count < cfg.mandatoryLimit
          ? `Add ${rem} More Item${rem > 1 ? 's' : ''} to Unlock ₹${dealPrice}`
          : `You've Unlocked ₹${dealPrice} Deal Price`;
        msgEl.style.marginBottom = '10px';
      }
    }

    // Images
    const imgBox = getEl('flexi-product-images');
    const lock = getEl('flexi-lock-icon');

    // Width config per mandatoryLimit: { desktop: px, mobile: px }
    const widthMap = {
      2: { desktop: 80, mobile: 60 },
      3: { desktop: 110, mobile: 90 },
      4: { desktop: 145, mobile: 105 },
      5: { desktop: 170, mobile: 130 },
      6: { desktop: 195, mobile: 150 },
      7: { desktop: 220, mobile: 175 },
      8: { desktop: 245, mobile: 180 }
    };

    if (count === 0) {
      imgBox.style.display = 'none';
      lock.style.display = 'block';
    } else {
      imgBox.style.display = 'flex';
      var isMobile = window.innerWidth <= 749;
      var w = widthMap[cfg.mandatoryLimit];
      if (w) {
        imgBox.style.width = (isMobile ? w.mobile : w.desktop) + 'px';
      } else {
        imgBox.style.width = (isMobile ? (35 + cfg.mandatoryLimit * 22.5) : (15 + cfg.mandatoryLimit * 27.5)) + 'px';
      }
      lock.style.display = 'none';

      // Multi-step: group images by step with per-step placeholders
      if (cfg.isMultiStep && window.FLEXI_STEPS) {
        let html = '';
        window.FLEXI_STEPS.forEach(step => {
          const stepItems = selected.filter(p => p.stepIndex === String(step.index));
          stepItems.forEach(p => {
            html += `<img src="${p.image}" alt="${p.title}" class="flexi-sticky-product-img" onerror="this.src='https://via.placeholder.com/50'">`;
          });
          const emptySlots = Math.max(0, step.mandatory - stepItems.length);
          html += Array(emptySlots).fill('<div class="flexi-sticky-empty-slot">+</div>').join('');
        });
        imgBox.innerHTML = html;
      } else {
        imgBox.innerHTML = selected.map(p =>
          `<img src="${p.image}" alt="${p.title}" class="flexi-sticky-product-img" onerror="this.src='https://via.placeholder.com/50'">`
        ).join('') + Array(Math.max(0, cfg.mandatoryLimit - count))
          .fill('<div class="flexi-sticky-empty-slot">+</div>')
          .join('');
      }
    }

    // Right section
    const right = getEl('flexi-cart-right');
    const savingsSection = getEl('flexi-savings-section');
    const itemValueSection = getEl('flexi-item-value-section');
    const isComplete = count === cfg.mandatoryLimit;

    if (count === 0) {
      right.style.display = 'none';
      getEl('flexi-expand-icon').style.display = 'none';
    } else {
      right.style.display = 'flex';
      getEl('flexi-expand-icon').style.display = 'block';

      // Show savings when all items checked, otherwise show item value
      if (isComplete) {
        itemValueSection.style.display = 'none';
        savingsSection.style.display = 'block';

        const savings = total - cfg.bundlePrice;
        getEl('flexi-savings-value').textContent = fmt(Math.max(0, savings));
        getEl('flexi-deal-price-value').textContent = fmtDec(cfg.bundlePrice);
        getEl('flexi-combined-price-value').textContent = fmtDec(total);
      } else {
        savingsSection.style.display = 'none';
        itemValueSection.style.display = 'block';
        getEl('flexi-price-value').textContent = fmtDec(total);
      }
    }

    // Pay button only enabled when all mandatory items selected
    const payNowBtn = getEl('flexi-pay-now');
    payNowBtn.disabled = count !== cfg.mandatoryLimit;
    payNowBtn.style.opacity = count === cfg.mandatoryLimit ? '1' : '0.5';
    payNowBtn.style.pointerEvents = count === cfg.mandatoryLimit ? 'auto' : 'none';
    payNowBtn.classList.toggle('ready-to-checkout', count === cfg.mandatoryLimit);

    // Show arrow in pay button when all items selected
    const payArrow = getEl('flexi-pay-arrow');
    if (payArrow) {
      payArrow.style.display = count === cfg.mandatoryLimit ? 'inline-block' : 'none';
    }

    if (count === 0) closePopup();

    renderPopup(total);
  };

  // RENDER POPUP
  const renderPopup = (total) => {
    const count = selected.length;
    const rem = cfg.mandatoryLimit - count;
    const imgBox = getEl('flexi-popup-images');

    // Update popup title with count
    const popupTitle = getEl('flexi-popup-title');
    if (popupTitle) {
      popupTitle.textContent = `Your Box (${count}/${cfg.mandatoryLimit})`;
    }

    // Render items in order (first added shows first)
    imgBox.innerHTML = selected.map((p, idx) => `
      <div class="flexi-popup-img-wrapper">
        <div class="flexi-popup-img-container">
          <img src="${p.image}" alt="${p.title}" class="flexi-popup-product-img" onerror="this.src='https://via.placeholder.com/80'">
          <button class="flexi-popup-img-close" type="button" data-remove data-index="${idx}" title="Remove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6L18 18"/></svg>
          </button>
        </div>
        <div class="flexi-popup-product-info">
          <div class="flexi-popup-product-title">${p.title}</div>
          <div class="flexi-popup-product-price">₹${fmtDec(p.price)}</div>
        </div>
      </div>
    `).join('') + Array(Math.max(0, cfg.mandatoryLimit - count))
      .fill('<div class="flexi-popup-empty-slot">+</div>')
      .join('');

    // Bill Summary
    const uTotal = selected.reduce((sum, p) => sum + p.price, 0);
    const isComplete = count === cfg.mandatoryLimit;

    getEl('flexi-summary-item-count').textContent = count;
    getEl('flexi-summary-subtotal').textContent = `₹${fmtDec(uTotal)}`;

    // Show save row only if all items selected
    const saveRow = getEl('flexi-summary-save');
    if (isComplete) {
      const savings = uTotal - cfg.bundlePrice;
      getEl('flexi-summary-save-amount').textContent = `₹${fmt(Math.max(0, savings))}`;
      saveRow.style.display = 'flex';
      getEl('flexi-summary-total').textContent = `₹${fmtDec(cfg.bundlePrice)}`;
    } else {
      saveRow.style.display = 'none';
      getEl('flexi-summary-total').textContent = `₹${fmtDec(uTotal)}`;
    }

    // Item Value and Savings Display (bottom section)
    const savingsSection = getEl('flexi-popup-savings-section');
    const itemValueSection = getEl('flexi-popup-item-value-section');

    if (isComplete) {
      itemValueSection.style.display = 'none';
      savingsSection.style.display = 'block';

      const savings = uTotal - cfg.bundlePrice;
      getEl('flexi-popup-savings-value').textContent = fmt(Math.max(0, savings));
      getEl('flexi-popup-deal-price-value').textContent = fmtDec(cfg.bundlePrice);
      getEl('flexi-popup-combined-price-value').textContent = fmtDec(uTotal);
    } else {
      savingsSection.style.display = 'none';
      itemValueSection.style.display = 'block';
      getEl('flexi-popup-price-value').textContent = fmtDec(uTotal);
    }

    // Popup pay button only enabled when all mandatory items selected
    const popupPayBtn = getEl('flexi-popup-pay-now');
    popupPayBtn.disabled = count !== cfg.mandatoryLimit;
    popupPayBtn.style.opacity = count === cfg.mandatoryLimit ? '1' : '0.5';
    popupPayBtn.style.pointerEvents = count === cfg.mandatoryLimit ? 'auto' : 'none';
    popupPayBtn.classList.toggle('ready-to-checkout', count === cfg.mandatoryLimit);

    // Show arrow in popup pay button when all items selected
    const popupPayArrow = getEl('flexi-popup-pay-arrow');
    if (popupPayArrow) {
      popupPayArrow.style.display = count === cfg.mandatoryLimit ? 'inline-block' : 'none';
    }

    // Add click handlers to empty slots
    document.querySelectorAll('.flexi-popup-empty-slot').forEach(slot => {
      slot.addEventListener('click', () => {
        closePopup();
      });
    });
  };

  // EVENTS
  const init = () => {
    const upd = debounce(update);

    // Global click handler to prevent drawer opening
    document.addEventListener('click', (e) => {
      const isPopupOpen = getEl('flexi-popup').style.display === 'block';
      // Allow close button, empty slot, and internal popup elements to work
      if (isPopupOpen && e.target.closest('.flexi-popup') && !e.target.closest('.flexi-popup-close') && !e.target.closest('.flexi-popup-empty-slot') && !e.target.closest('#flexi-popup-pay-now')) {
        e.stopPropagation();
      }
    }, true); // Use capture phase to intercept before other handlers

    // Sticky bar click
    getEl('flexi-sticky-bar').addEventListener('click', (e) => {
      if (!e.target.closest('.flexi-pay-btn') && !e.target.closest('button') && selected.length > 0) {
        openPopup();
      }
    });

    // Close - prevent propagation
    getEl('flexi-popup-close').addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      closePopup();
    });
    getEl('flexi-popup-overlay').addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      closePopup();
    });

    // Remove item from popup - click the quantity-minus button of same product
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.flexi-popup-img-close');
      if (!closeBtn) return;

      e.stopPropagation();
      e.preventDefault();

      const idx = parseInt(closeBtn.dataset.index);
      if (isNaN(idx) || idx < 0 || idx >= selected.length) return;

      const item = selected[idx];
      if (!item?.quantityInput) return;

      // Find the collection-item and then find its quantity-minus button
      const collectionItem = item.quantityInput.closest('.collection-item');
      if (!collectionItem) return;

      const minusBtn = collectionItem.querySelector('.quantity-minus');
      if (minusBtn) {
        minusBtn.click();
      }
    }, true);

    // Pay - prevent propagation and prevent cart drawer
    getEl('flexi-pay-now').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      document.getElementById('submit-btn')?.click();
    });
    getEl('flexi-popup-pay-now').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      closePopup();
      getEl('flexi-pay-now').click();
    });

    // Product changes
    document.addEventListener('change', (e) => {
      if ((e.target.type === 'checkbox' && e.target.closest('.collection-item')) ||
          e.target.classList.contains('quantity-input')) {
        // Direct update for checkbox changes, debounced for quantity
        if (e.target.type === 'checkbox') {
          update();
        } else {
          upd();
        }
      }
    });

    // Quantity +/- buttons - with delay to allow DOM update
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quantity-plus') ||
          e.target.classList.contains('quantity-minus')) {
        // Wait for DOM to update before recalculating
        setTimeout(upd, 50);
      }
    });

    // Listen for quantity input value changes (for programmatic updates)
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        upd();
      }
    });
  };

  // AUTO-INIT
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // EXPOSE API
  window.stickyCart = { update, openPopup, closePopup };
})();
